import { lazy } from "react";

// React.lazy for route pages, made safe across deployments.
//
// Every page is its own hashed chunk (Invoices.4c7fe094.chunk.js, ...). After a
// new release the old chunk files are gone from the server, but a user who
// still has the previous version open (or an old index.html cached) asks for
// the OLD chunk name the next time they open a page -> "Loading chunk N
// failed" and a broken/blank screen until they hard-refresh or clear their
// cache. That is the failure this guards against: on a chunk-load error we
// reload once, which fetches the current index.html and current chunk names.
//
// The timestamp guard stops an endless reload loop if the chunk is genuinely
// unavailable (server down, real 404) - a second failure inside the window is
// surfaced to the normal error handling instead.
const RELOAD_KEY = "chunk-error-reload-at";
const RELOAD_WINDOW_MS = 15000;

const isChunkLoadError = (error) =>
  error?.name === "ChunkLoadError" ||
  /Loading (CSS )?chunk [\w-]+ failed|dynamically imported module|error loading dynamically/i.test(
    error?.message || ""
  );

export const lazyWithRetry = (factory) =>
  lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (isChunkLoadError(error) && navigator.onLine !== false) {
        let lastReload = 0;
        try {
          lastReload = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
        } catch (e) {
          // storage blocked - fall through and treat as "never reloaded"
        }
        if (Date.now() - lastReload > RELOAD_WINDOW_MS) {
          try {
            sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          } catch (e) {
            // ignore
          }
          window.location.reload();
          // Never settles: keeps React showing the Suspense fallback for the
          // instant before the reload takes over, rather than an error flash.
          return new Promise(() => {});
        }
      }
      throw error;
    }
  });
